import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getScanner, fmtNum, fmtPrice } from "@/lib/api";
import { Sparkline } from "@/components/Sparkline";
import { Star, ArrowUp, ArrowDown, Flame, Zap } from "lucide-react";

const CATS = [
  { id: "all", label: "All" },
  { id: "gainers", label: "Top Gainers" },
  { id: "losers", label: "Top Losers" },
  { id: "high_volume", label: "High Volume" },
  { id: "breakout", label: "Breakouts" },
  { id: "penny", label: "Penny" },
  { id: "adr", label: "ADRs" },
];

const SignalBadge = ({ signal }) => {
  const map = {
    bullish: "bg-bull/10 text-bull border-bull/25",
    bearish: "bg-bear/10 text-bear border-bear/25",
    neutral: "bg-surface-2 text-secondary border-border",
  };
  return (
    <span className={`font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[signal]}`}>
      {signal}
    </span>
  );
};

export const ScannerTable = ({ onSelect, onStar, starred = [] }) => {
  const [cat, setCat] = useState("all");
  const { data = [], isLoading } = useQuery({
    queryKey: ["scanner", cat],
    queryFn: () => getScanner(cat),
    refetchInterval: 6000,
  });

  return (
    <div className="bg-surface border border-border rounded-none md:rounded-lg overflow-hidden" data-testid="scanner-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-app/60">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-ai" />
          <h3 className="text-sm font-semibold uppercase tracking-[0.1em] text-white">Live Scanner</h3>
          <span className="live-dot h-1.5 w-1.5 rounded-full bg-bull ml-1" />
        </div>
        <span className="text-[10px] font-mono text-tertiary uppercase">Auto-refresh 15s</span>
      </div>

      <div className="flex gap-1 overflow-x-auto px-2 py-2 border-b border-border-subtle no-scrollbar">
        {CATS.map((c) => (
          <button
            key={c.id}
            data-testid={`scanner-cat-${c.id}`}
            onClick={() => setCat(c.id)}
            className={`whitespace-nowrap text-xs font-mono px-3 py-1.5 rounded transition-colors ${
              cat === c.id ? "bg-neutral text-white" : "text-secondary hover:bg-surface-2"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-tertiary font-mono border-b border-border">
              <th className="text-left py-2 px-3 font-normal">Ticker</th>
              <th className="text-right py-2 px-3 font-normal">Price</th>
              <th className="text-right py-2 px-3 font-normal">Chg%</th>
              <th className="text-right py-2 px-3 font-normal hidden sm:table-cell">RSI</th>
              <th className="text-right py-2 px-3 font-normal hidden md:table-cell">VWAP</th>
              <th className="text-right py-2 px-3 font-normal hidden sm:table-cell">Rel Vol</th>
              <th className="text-right py-2 px-3 font-normal hidden lg:table-cell">Volume</th>
              <th className="text-center py-2 px-3 font-normal hidden md:table-cell">Trend</th>
              <th className="text-center py-2 px-3 font-normal">Signal</th>
              <th className="py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={10} className="text-center py-10 text-tertiary font-mono text-xs">Loading market…</td></tr>
            )}
            {data.map((s) => {
              const up = s.change_pct >= 0;
              const rsiColor = s.rsi >= 70 ? "text-bear" : s.rsi <= 30 ? "text-bull" : "text-secondary";
              const isStar = starred.includes(s.symbol);
              return (
                <tr
                  key={s.symbol}
                  data-testid={`scanner-row-${s.symbol}`}
                  onClick={() => onSelect(s.symbol)}
                  className="border-b border-border-subtle hover:bg-surface-2 transition-colors cursor-pointer group"
                >
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-white">{s.symbol}</span>
                      {s.breakout && <Flame size={12} className="text-warn" />}
                      {s.breakdown && <ArrowDown size={12} className="text-bear" />}
                    </div>
                    <div className="text-[10px] text-tertiary truncate max-w-[120px]">{s.name}</div>
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-white">${fmtPrice(s.price)}</td>
                  <td className={`py-2.5 px-3 text-right font-mono ${up ? "text-bull" : "text-bear"}`}>
                    <span className="inline-flex items-center gap-0.5 justify-end">
                      {up ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                      {up ? "+" : ""}{s.change_pct}%
                    </span>
                  </td>
                  <td className={`py-2.5 px-3 text-right font-mono hidden sm:table-cell ${rsiColor}`}>{s.rsi}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-secondary hidden md:table-cell">${fmtPrice(s.vwap)}</td>
                  <td className={`py-2.5 px-3 text-right font-mono hidden sm:table-cell ${s.rel_volume >= 1.5 ? "text-warn" : "text-secondary"}`}>{s.rel_volume}x</td>
                  <td className="py-2.5 px-3 text-right font-mono text-secondary hidden lg:table-cell">{fmtNum(s.volume)}</td>
                  <td className="py-2.5 px-3 hidden md:table-cell w-24"><Sparkline data={s.spark} up={up} /></td>
                  <td className="py-2.5 px-3 text-center"><SignalBadge signal={s.signal} /></td>
                  <td className="py-2.5 px-2 text-center">
                    <button
                      data-testid={`star-${s.symbol}`}
                      onClick={(e) => { e.stopPropagation(); onStar(s.symbol); }}
                      className="opacity-40 group-hover:opacity-100 transition-opacity"
                    >
                      <Star size={14} className={isStar ? "fill-warn text-warn opacity-100" : "text-secondary"} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
